/**
 * Centralized auth utilities.
 * All auth-state cleanup goes through clearAuthState() so that
 * every logout path removes the same set of keys.
 */

// When the current session was created. Written ONLY by a successful login,
// never by activity tracking, so the apiClient 401 handler can tell "this 401
// raced a brand-new login" apart from "this session has expired".
// (lastActivity cannot do that job: useSessionTimeout rewrites it on every
// page load and on user activity.)
export const LOGIN_AT_KEY = 'loginAt';
export const LOGIN_GRACE_MS = 10000;

const AUTH_KEYS = [
  'authToken',
  'userRole',
  'userEmail',
  'userId',
  'userName',
  'lastActivity',
  LOGIN_AT_KEY,
];

/**
 * Remove all auth-related data from localStorage.
 * Call this from every logout / session-expiry path.
 */
export function clearAuthState() {
  AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
}

/** Record that a session was just created. Call after a successful login. */
export function markLoggedIn(now = Date.now()) {
  localStorage.setItem(LOGIN_AT_KEY, String(now));
}

/** True within LOGIN_GRACE_MS of the last successful login. */
export function isWithinLoginGrace(now = Date.now()) {
  const loginAt = parseInt(localStorage.getItem(LOGIN_AT_KEY), 10);
  return Number.isFinite(loginAt) && now - loginAt >= 0 && now - loginAt < LOGIN_GRACE_MS;
}

// ── returnUrl (deep-link survivability through login) ───────────────────────
// A logged-out user who clicks a deep link (e.g. an email's "See Full Detail"
// button) must land back on that exact URL after Microsoft SSO. The MSAL
// redirect fallback (loginRedirect) leaves the page and returns to
// window.location.origin ("/") with NO query string, so the intended URL is
// carried in sessionStorage (seeded from a ?returnUrl= param on the login
// surface). Popup logins never navigate away, so the param survives in-page;
// stashing to sessionStorage covers both flows uniformly.
export const RETURN_URL_STORAGE_KEY = "postLoginReturnUrl";

/**
 * True if the string contains any ASCII control character (0x00-0x1f or 0x7f).
 * Written with charCodeAt (not a regex literal) to keep the source pure ASCII.
 */
function hasControlChar(s) {
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    if (c < 32 || c === 127) return true;
  }
  return false;
}

/**
 * Open-redirect guard: accept ONLY a same-origin relative path.
 * Rejects absolute/protocol-relative URLs, backslash tricks, control chars,
 * and the login surface itself (to avoid pointless loops). Input is expected
 * already once-decoded (as URLSearchParams / sessionStorage return it).
 * @returns {string|null} the safe relative path, or null if unusable.
 */
export function sanitizeReturnUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (raw[0] !== "/") return null;                    // must be a relative path
  if (raw[1] === "/" || raw[1] === "\\") return null; // protocol-relative / UNC
  if (raw.includes("\\")) return null;                // backslash host trick
  if (hasControlChar(raw)) return null;               // control chars
  if (raw === "/" || raw.startsWith("/login")) return null; // not the login surface
  return raw;
}

/**
 * Build the login path carrying the intended destination as a query param.
 * Used by both the React route guard (Navigate) and the apiClient 401 handler
 * (window.location.href) — a query param is the only carrier that survives the
 * 401 hard-redirect (which destroys React Router state).
 */
export function buildLoginPath(targetPathAndSearch) {
  const safe = sanitizeReturnUrl(targetPathAndSearch);
  return safe ? `/login?returnUrl=${encodeURIComponent(safe)}` : "/login";
}

/**
 * Read ?returnUrl= from the current URL and stash a validated copy in
 * sessionStorage so it survives the SSO redirect round-trip. Safe to call on
 * every login-surface mount; no-op when absent/invalid.
 */
export function captureReturnUrlFromLocation() {
  try {
    const raw = new URLSearchParams(window.location.search).get("returnUrl");
    const safe = sanitizeReturnUrl(raw);
    if (safe) sessionStorage.setItem(RETURN_URL_STORAGE_KEY, safe);
  } catch (_) {
    /* sessionStorage unavailable — fall back to default routing */
  }
}

/**
 * Pop the stashed returnUrl (one-shot): returns a validated relative path and
 * clears it, or null. Callers fall back to the role home page on null.
 */
export function consumeReturnUrl() {
  try {
    const v = sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
    if (v) sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
    return sanitizeReturnUrl(v);
  } catch (_) {
    return null;
  }
}
